import React from 'react';
import { createPortal } from 'react-dom';
import { Check, X } from 'lucide-react';
import { AppModal } from '../../../components/AppModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import type { PositionQuoteOffer, PositionQuoteOffers } from '../quoteComparison';

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 4 });
const qtyFmt = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });

export type QuoteOffersPositionContext = {
  positionId: string;
  label: string;
  quantity: number;
  unit: string;
  currentEk: number;
  appliedQuoteItemId: string | null;
};

interface Props {
  open: boolean;
  onClose: () => void;
  position: QuoteOffersPositionContext | null;
  offers: PositionQuoteOffers | null;
  applying: boolean;
  canApply: boolean;
  onApply: (offer: PositionQuoteOffer | null) => void | Promise<void>;
}

function statusLabel(o: PositionQuoteOffer): string {
  if (o.isAlternative) return 'Alternativ';
  if (o.status === 'proposed') return 'Vorschlag';
  if (o.status === 'confirmed') return o.source === 'manual' ? 'manuell' : 'zugeordnet';
  return '';
}

/** Angebote je LV-Position: Lieferant, EK je LV-Einheit, Status; Übernehmen in den EK. */
export const PositionQuoteOffersPopover: React.FC<Props> = ({ open, onClose, position, offers, applying, canApply, onApply }) => {
  const appliedQuoteId = React.useMemo(() => {
    if (!position?.appliedQuoteItemId || !offers) return null;
    return offers.offers.find((o) => o.lines.some((l) => l.itemId === position.appliedQuoteItemId))?.quoteId ?? null;
  }, [offers, position?.appliedQuoteItemId]);

  if (!open) return null;

  // Portal: fixed-Overlay darf nicht von einem Eltern-Container (transform/overflow) versetzt werden.
  return createPortal(
    <AppModal open={open} onClose={onClose} panelClassName="md:w-[calc(100vw-6rem)] md:max-w-6xl md:mx-auto">
      <div className="flex flex-col max-h-[90vh]">
        <div className="flex items-start justify-between gap-3 border-b border-app-border px-6 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-app-text truncate">Lieferantenangebote zur Position</h2>
            {position && (
              <p className="text-sm text-app-text-subtle mt-0.5">
                {position.label} · {qtyFmt.format(position.quantity)} {position.unit} · aktueller EK{' '}
                <span className="text-app-text">{euro.format(position.currentEk)}</span>
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-app-hover text-slate-400" aria-label="Schließen">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="app-scrollbar min-h-0 flex-1 overflow-auto">
          {!offers || offers.offers.length === 0 ? (
            <p className="text-sm text-app-text-muted px-6 py-6">Keine zugeordneten Angebotszeilen.</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-app-surface text-app-text-subtle z-10">
                <tr>
                  <th className="pl-6 pr-3 py-2 text-left font-medium whitespace-nowrap">Lieferant</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Datum</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Art.-Nr.</th>
                  <th className="px-3 py-2 text-left font-medium">Bezeichnung</th>
                  <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Menge</th>
                  <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Faktor</th>
                  <th className="px-3 py-2 text-right font-medium whitespace-nowrap">EK / {position?.unit || 'Einheit'}</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Status</th>
                  <th className="pl-3 pr-6 py-2 text-right font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {offers.offers.map((o) => {
                  const cheapest = o.quoteId === offers.cheapestQuoteId;
                  const applied = o.quoteId === appliedQuoteId;
                  return (
                    <tr
                      key={o.quoteId}
                      className={`border-t border-app-border align-top ${applied ? 'bg-emerald-50/50' : o.isAlternative ? 'bg-amber-50' : ''}`}
                      title={o.isAlternative ? 'Alternativposition des Händlers' : undefined}
                    >
                      <td className="pl-6 pr-3 py-2 font-medium text-app-text whitespace-nowrap">
                        {o.supplierName}
                        {o.deliveryTime && <span className="block font-normal text-app-text-subtle">{o.deliveryTime}</span>}
                      </td>
                      <td className="px-3 py-2 text-app-text-muted tabular-nums whitespace-nowrap">
                        {o.quoteDate ? new Date(o.quoteDate).toLocaleDateString('de-DE') : ''}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                        {o.lines.map((l) => (
                          <span key={l.itemId} className="block">
                            {l.articleNumber ?? ''}
                          </span>
                        ))}
                      </td>
                      <td className="px-3 py-2 min-w-[18rem] text-app-text">
                        {o.lines.map((l) => (
                          <span key={l.itemId} className="block">
                            {l.itemName}
                          </span>
                        ))}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                        {o.lines.map((l) => (
                          <span key={l.itemId} className="block">
                            {l.quantity != null ? `${qtyFmt.format(l.quantity)} ${l.unit ?? ''}` : ''}
                          </span>
                        ))}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-app-text-muted whitespace-nowrap">
                        {o.lines.map((l) => (
                          <span key={l.itemId} className="block">
                            {l.qtyFactor !== 1 ? `×${l.qtyFactor}` : ''}
                          </span>
                        ))}
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold whitespace-nowrap ${cheapest ? 'text-emerald-700' : 'text-app-text'}`}>
                        {o.unitEkPerLvUnit != null ? euro.format(o.unitEkPerLvUnit) : ''}
                        {cheapest && <span className="block text-sm font-normal text-emerald-700">günstigster</span>}
                      </td>
                      <td className="px-3 py-2 text-app-text-subtle whitespace-nowrap">
                        {statusLabel(o)}
                        {o.confidence != null && o.status !== 'confirmed' ? ` (${Math.round(o.confidence * 100)} %)` : ''}
                      </td>
                      <td className="pl-3 pr-6 py-2 text-right whitespace-nowrap">
                        {applied ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-sm font-semibold">
                            <Check className="w-4 h-4" /> übernommen
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={!canApply || applying || o.unitEkPerLvUnit == null}
                            onClick={() => void onApply(o)}
                            className="inline-flex items-center gap-1 rounded-md bg-pipedrive-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            title={!canApply ? 'Recht „Projekte bearbeiten“ nötig' : o.isAlternative ? 'Alternativposition übernehmen' : 'EK übernehmen'}
                          >
                            {applying ? <LoadingSpinner size="xs" /> : null}
                            Übernehmen
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {appliedQuoteId && canApply && (
          <div className="border-t border-app-border px-6 py-3 flex justify-end">
            <button
              type="button"
              disabled={applying}
              onClick={() => void onApply(null)}
              className="text-sm text-app-text-muted hover:text-red-700"
            >
              Übernahme zurücknehmen (EK auf 0)
            </button>
          </div>
        )}
      </div>
    </AppModal>,
    document.body,
  );
};
